"use client";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
    Index,
    Hits,
    Configure,
    useInstantSearch,
    InstantSearch,
    type InstantSearchProps,
    useHits,
} from "react-instantsearch";
import { history as historyRouter } from "instantsearch.js/es/lib/routers";
import { useTranslations } from "../hooks/useTranslations";
import { ItemList } from "./components/ItemList";
import { NoResults, NoResultsBoundary } from "./components/NoResultsBoundary";
import { useConfigContext } from "../ConfigProvider";
import { InformationHit } from "../app-instantsearch/components/informationHit";
import { SearchBox } from "../app-instantsearch/components/SearchBox";
import { FilterBar } from "./filter/FilterBar";
import { useIntersectionObserver } from "../utils/useIntersectionObserver";
import { seoFriendlyStateMapping } from "./seoFriendlyStateMapping";
import type { AppRouteState } from "../types/types";
import { DateFilterProvider } from "../app-instantsearch/providers/DateFilterProvider.tsx";
import { UIProvider } from "../app-instantsearch/providers/UIProvider.tsx";
import { showItemsHelper, showInformationHelper } from "../utils/historyHelpers.ts";
import { algoliaListUpdate } from "../app-instantsearch/utils/algoliaListUpdate";
import { PriceOverrideProvider, usePriceOverrideContext } from "./providers/PriceOverrideProvider";
import { initAlgoliaInsights } from "../utils/insights";



function FireImpressionsOnceHitsChange({ enabled }: { enabled: boolean }) {
    const { hits } = useHits<any>();

    useEffect(() => {
        if (!enabled) return;
        if (!hits?.length) return;

        let raf1 = 0;
        let raf2 = 0;

        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                try {
                    algoliaListUpdate();
                } catch (e) {
                    // Ignore algolia update errors
                }
            });
        });

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, [enabled, hits]);

    return null;
}

function FetchDebitorPricesOnHitsChange({ enabled }: { enabled: boolean }) {
    const { hits } = useHits<any>();
    const { requestPrices } = usePriceOverrideContext();

    useEffect(() => {
        if (!enabled || !hits?.length) return;

        requestPrices(
            hits.map((hit) => ({ itemNo: hit.itemNo, itemId: hit.itemId }))
        );
    }, [enabled, hits, requestPrices]);

    return null;
}


function InnerContent({
                          algoliaIndexName,
                          isSearch,
                          view,
                          setView,
                      }: {
    algoliaIndexName: string;
    isSearch: boolean;
    view: "items" | "information";
    setView: (v: "items" | "information") => void;
}) {
    const { scopedResults } = useInstantSearch()
    const tc = useTranslations();

    const itemsIndexName = `${algoliaIndexName}`;
    const baseIndexName = algoliaIndexName.replace(/_GROUPED$/, "");
    const informationIndexName = `${baseIndexName}_content_crawler_pages`;

    const results = scopedResults ?? [];
    const itemsResult = results.find((r) => r.indexId === itemsIndexName)?.results;
    const infoResult = results.find((r) => r.indexId === informationIndexName)?.results;

    const itemsCount = itemsResult?.nbHits || 0;
    const infoCount = infoResult?.nbHits || 0;

    const siteCode = typeof document !== "undefined" ? document.body.dataset.site_code : undefined;
    const isAEWI = siteCode === "aewi";

    const showItems = showItemsHelper(setView);
    const showInformation = showInformationHelper(setView);

    const itemsViewEnabled = view === "items";

    const { algoliaConfig } = useConfigContext();
    const navHtml = algoliaConfig.navigationHtml || "";

    useEffect(() => {
        if (typeof window === "undefined") return;

        const target = document.querySelector(".ais-subnavigation");
        if (!target) return;

        const enhanceNav = () => {
            const navRoot = target.querySelector(".subnavigation");
            if (!navRoot) return;

            const navItems = navRoot.querySelectorAll<HTMLLIElement>("ul > li");

            navItems.forEach((li) => {
                const subUl = li.querySelector("ul");
                const link = li.querySelector("a");

                if (subUl && link && !link.querySelector(".iconNew")) {
                    const icon = document.createElement("i");
                    icon.className = "iconNew iconNew-plus";
                    link.appendChild(icon);

                    icon.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        li.classList.toggle("open");
                    });
                }

                if (li.classList.contains("active_tree") || li.querySelector("a.active")) {
                    li.classList.add("open");
                }
            });
        };

        enhanceNav();

        const observer = new MutationObserver(() => {
            enhanceNav();
        });

        observer.observe(target, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [navHtml]);



    return (
        <>
            <div suppressHydrationWarning>
                <SearchBox />
            </div>

            {isSearch && (
                <div className="ais-top">
                    <div className="ais-layout__viewToggle">
            <span className={itemsViewEnabled ? "active" : ""} onClick={showItems}>
              {tc.results_in_items} ({itemsCount})
            </span>

                        {infoCount > 0 && (
                            <span className={view === "information" ? "active" : ""} onClick={showInformation}>
                {tc.information} ({infoCount})
              </span>
                        )}
                    </div>
                </div>
            )}

            <div className="ais-layout">
                <div className="ais-layout__header">
                    <div className="ais-layout__filter">
                        {itemsViewEnabled && !(isAEWI && !isSearch) && <FilterBar />}
                    </div>
                </div>

                <section className="ais-layout__section">

                    {!isSearch && (
                        <div
                            className="ais-subnavigation"
                            dangerouslySetInnerHTML={{ __html: navHtml }}
                            suppressHydrationWarning
                        />
                    )}

                    <div className="ais-layout__main" suppressHydrationWarning>
                        <NoResultsBoundary fallback={<NoResults />}>
                            <div style={{ display: itemsViewEnabled ? "block" : "none" }} suppressHydrationWarning>
                                <ItemList />
                                <FireImpressionsOnceHitsChange enabled={itemsViewEnabled} />
                                <FetchDebitorPricesOnHitsChange enabled={itemsViewEnabled} />
                            </div>
                        </NoResultsBoundary>

                        <Index indexName={informationIndexName}>
                            <Configure filters="" />
                            {view === "information" && (
                                <Hits hitComponent={InformationHit} />
                            )}
                        </Index>
                    </div>
                </section>
            </div>
        </>
    );
}

export function AppInstantsearch({ serverUrl }: { serverUrl?: URL }) {
    const { algoliaConfig } = useConfigContext();
    useEffect(() => {
        initAlgoliaInsights(algoliaConfig);
    }, [algoliaConfig]);

    const {
        algoliaConfig: { algoliaIndexName, algoliaShopFilter, isSearch },
        searchClient,
        algoliaRefinements,
    } = useConfigContext();

    const mainIndexName = `${algoliaIndexName}`;


    const headerRef = useRef<HTMLElement>(null);
    useIntersectionObserver(headerRef, "is-sticky");

    const onStateChange = useCallback<NonNullable<InstantSearchProps["onStateChange"]>>(
        ({ uiState, setUiState }) => {
            setUiState(uiState);
        },
        []
    );

    function urlToLocation(u: URL): Location {
        const locLike = {
            href: u.href,
            origin: u.origin,
            protocol: u.protocol,
            host: u.host,
            hostname: u.hostname,
            port: u.port,
            pathname: u.pathname,
            search: u.search,
            hash: u.hash,
            assign: () => {},
            reload: () => {},
            replace: () => {},
            ancestorOrigins: { length: 0, item: () => null, contains: () => false } as any,
        } as any;

        return locLike as unknown as Location;
    }

    const router = historyRouter<AppRouteState>({
        getLocation: () =>
            typeof window === "undefined"
                ? urlToLocation(serverUrl ?? new URL("http://localhost/"))
                : window.location,
    });

    const [view, setView] = useState<"items" | "information">("items");

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash === "#information") {
            setView("information");
        }
    }, []);

    const stateMapping = useMemo(() => {
        const refinementListConfig = algoliaRefinements?.refinementList || [];
        const sorting: Array<{ label: string; code?: string; value: string }> = [];


        return seoFriendlyStateMapping(sorting, mainIndexName, refinementListConfig);
    }, [algoliaRefinements, mainIndexName]);

    return (
        <DateFilterProvider>
            <InstantSearch
                searchClient={searchClient}
                indexName={mainIndexName}
                onStateChange={onStateChange}
                insights={true}
                routing={{
                    router: router,
                    stateMapping: stateMapping,
                }}
                initialUiState={{ [mainIndexName]: { hitsPerPage: 999 } }}
            >
                <Configure
                    filters={!isSearch ? algoliaShopFilter : undefined}
                    maxValuesPerFacet={100}
                    hitsPerPage={999}
                />

                <PriceOverrideProvider>
                    <UIProvider>
                        <InnerContent
                            algoliaIndexName={algoliaIndexName}
                            isSearch={isSearch}
                            view={view}
                            setView={setView}
                        />
                    </UIProvider>
                </PriceOverrideProvider>
            </InstantSearch>
        </DateFilterProvider>
    );
}
